import { Box, Typography } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import BasicTabs from 'components/tabs/basic-tabs/basic-tabs.component'
import TabPanel from 'components/tabs/tab-panel/tab-panel.component'
import ActionButtons from 'components/action-buttons/action-buttons.component'
import { customerDetailTabs } from 'pages/address/address.constant'
import {
  AddressContext,
  AddressProviderValue,
} from 'pages/address/address.provider'
import { useCustomerList, useSelectedCustomer } from 'pages/address/address.hooks'
import {
  useAddCompanyEmailMutation,
  useAddCompanyPhoneMutation,
} from 'store/company/company.api'
import { EmailType, PhoneType } from 'store/company/company.types'
import { formatPhoneForSave } from 'utils/phone'
import CustomerCompanyNameTab from './customer-company-name-tab/customer-company-name-tab.component'
import CustomerAddressTab from './customer-address-tab/customer-address-tab.component'
import CustomerEmailTab from './customer-email-tab/customer-email-tab.component'
import CustomerPhoneTab from './customer-phone-tab/customer-phone-tab.component'
import CustomerDocumentTab from './customer-document-tab/customer-document-tab.component'
import CustomerContactAddForm from './customer-contact-add-form/customer-contact-add-form.component'

const CustomerDetails = () => {
  const { detailTabIndex, setDetailTabIndex } =
    useContext(AddressContext) as AddressProviderValue
  const selectedCustomer = useSelectedCustomer()
  const { refetch } = useCustomerList()

  const [isDirty, setIsDirty] = useState(false)
  const [saveToken, setSaveToken] = useState(0)
  const [emailContactName, setEmailContactName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [newEmailType, setNewEmailType] =
    useState<EmailType>('NotificationEmail')

  const [phoneContactName, setPhoneContactName] = useState('')
  const [newPhone, setNewPhone] = useState('')
  const [newPhoneType, setNewPhoneType] = useState<PhoneType>('Dispatch')

  const [addEmail, { isLoading: isAddingEmail }] = useAddCompanyEmailMutation()
  const [addPhone, { isLoading: isAddingPhone }] = useAddCompanyPhoneMutation()

  const companyId = selectedCustomer?.association.companyId ?? ''

  useEffect(() => {
    setDetailTabIndex(0)
    setIsDirty(false)
    setEmailContactName('')
    setNewEmail('')
    setPhoneContactName('')
    setNewPhone('')
  }, [selectedCustomer?.association.id, setDetailTabIndex])

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setDetailTabIndex(newValue)
    setIsDirty(false)
  }

  const handleUpdate = () => {
    setSaveToken((prev) => prev + 1)
  }

  const handleSaved = () => {
    setIsDirty(false)
    refetch()
  }

  const handleAddEmail = async () => {
    const trimmed = newEmail.trim()
    if (!trimmed || !companyId) return
    try {
      await addEmail({
        companyId,
        body: {
          email: trimmed,
          emailType: newEmailType,
          userType: 'company',
          contactName: emailContactName.trim(),
        },
      }).unwrap()
      setNewEmail('')
      setEmailContactName('')
    } catch (e) {
      console.error('Failed to add email', e)
    }
  }

  const handleAddPhone = async () => {
    const trimmed = formatPhoneForSave(newPhone.trim())
    if (!trimmed || !companyId) return
    try {
      await addPhone({
        companyId,
        body: {
          phone: trimmed,
          phoneType: newPhoneType,
          userType: 'company',
          contactName: phoneContactName.trim(),
        },
      }).unwrap()
      setNewPhone('')
      setPhoneContactName('')
    } catch (e) {
      console.error('Failed to add phone', e)
    }
  }

  if (!selectedCustomer) {
    return (
      <Box p={3}>
        <Typography variant="body2" fontWeight={600}>
          Select a customer to view details.
        </Typography>
      </Box>
    )
  }

  const association = selectedCustomer.association
  const tabProps = {
    association,
    onDirtyChange: setIsDirty,
    saveToken,
    onSaved: handleSaved,
  }

  const showContactAddForm = detailTabIndex === 2 || detailTabIndex === 3
  const updateDisabled = detailTabIndex > 1 || !isDirty

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <BasicTabs
          tabs={customerDetailTabs}
          value={detailTabIndex}
          handleChange={handleTabChange}
          scrollableContent
        >
          <TabPanel value={detailTabIndex} index={0}>
            <CustomerCompanyNameTab {...tabProps} />
          </TabPanel>
          <TabPanel value={detailTabIndex} index={1}>
            <CustomerAddressTab {...tabProps} />
          </TabPanel>
          <TabPanel value={detailTabIndex} index={2}>
            <CustomerEmailTab association={association} />
          </TabPanel>
          <TabPanel value={detailTabIndex} index={3}>
            <CustomerPhoneTab association={association} />
          </TabPanel>
          <TabPanel value={detailTabIndex} index={4}>
            <CustomerDocumentTab companyId={companyId} />
          </TabPanel>
        </BasicTabs>
      </Box>

      {showContactAddForm && (
        <CustomerContactAddForm
          mode={detailTabIndex === 2 ? 'email' : 'phone'}
          contactName={
            detailTabIndex === 2 ? emailContactName : phoneContactName
          }
          value={detailTabIndex === 2 ? newEmail : newPhone}
          typeValue={detailTabIndex === 2 ? newEmailType : newPhoneType}
          onContactNameChange={
            detailTabIndex === 2 ? setEmailContactName : setPhoneContactName
          }
          onValueChange={detailTabIndex === 2 ? setNewEmail : setNewPhone}
          onTypeChange={(value) => {
            if (detailTabIndex === 2) {
              setNewEmailType(value as EmailType)
            } else {
              setNewPhoneType(value as PhoneType)
            }
          }}
          onAdd={detailTabIndex === 2 ? handleAddEmail : handleAddPhone}
          isAdding={detailTabIndex === 2 ? isAddingEmail : isAddingPhone}
        />
      )}

      <ActionButtons
        firstBtnProps={{
          title: 'Update',
          onClick: handleUpdate,
          disabled: updateDisabled,
          bgcolor: 'primary.main',
        }}
      />
    </Box>
  )
}

export default CustomerDetails
